/**
 * ControlPanel API (`/device-registrations`) クライアント (SBI-5)。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 端末ID⇄編成番号の紐づけ + 動作ステータスの一覧を取得する。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './broadcastPoints';
import type { ApiErrorBody, DeviceRegistrationListResponse } from './types';

export interface DeviceRegistrationsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class DeviceRegistrationsClient {
  private readonly options: DeviceRegistrationsClientOptions;

  constructor(options: DeviceRegistrationsClientOptions) {
    this.options = options;
  }

  /**
   * GET /device-registrations
   *
   * 端末ID⇄編成番号の最新紐づけ + 動作ステータスの一覧を取得する。
   * 現在は全件取得のみ対応（ページング機能は将来実装予定）。
   *
   * @throws Error - 204 No Content が返された場合（エラーとして扱う）
   */
  async list(): Promise<DeviceRegistrationListResponse> {
    const result = await this.request<DeviceRegistrationListResponse | undefined>(
      'GET',
      'device-registrations',
    );
    if (result === undefined) {
      throw new Error('Unexpected 204 No Content from GET /device-registrations');
    }
    return result;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? 'API リクエストに失敗しました';
      throw new ApiError(res.status, message, errorBody);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }
}

/** 端末種別。`device_id` プレフィックスからフロント側で導出する。 */
export type DeviceType = 'Web' | 'Android' | '不明';

/**
 * `device_id` のプレフィックスから端末種別を導出する。
 *
 * - `web-` / `web` 始まり → Web
 * - `and-` / `android` 始まり → Android
 * - `ios-` は iOS 端末だが、現状の一覧では Web/Android の 2 分類のみを扱うため
 *   判定対象外 (不明) とする。
 * - それ以外 / 空 → 不明
 *
 * 判定は大文字小文字を区別しない。
 */
export function deviceTypeFromId(deviceId: string | null | undefined): DeviceType {
  if (!deviceId) return '不明';
  const id = deviceId.toLowerCase();
  if (id.startsWith('web-') || id.startsWith('web')) return 'Web';
  if (id.startsWith('and-') || id.startsWith('android')) return 'Android';
  return '不明';
}
